import React from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import cx from 'classnames';
import moment from 'moment';

import messages from '../MLSummary.messages'
import styles from '../MLSummary.module.scss'

function MLInsured({ data, insuredsVM, show, deedInfo_Ext, dwellingDeedInfo_Ext, isModernProduct }) {
    const translator = useTranslator();

    if (_.isEmpty(data)) {
        return null; 
    }

    const getInsuredName = (insured) => {
        const nameAndTitle = insured.policyContactDisplayName_Ext ? insured.policyContactDisplayName_Ext : insured.displayName;
        let insuredName = '';

        if (!nameAndTitle) {
            return '';
        }

        for (let i = 0; i < nameAndTitle.length; i++) {
            if (nameAndTitle[i] !== ',') {
                insuredName = insuredName.concat(nameAndTitle[i]);
            } else {
                break;
            }
        }

        return insuredName;
    };

    const insuredHasMaritalStatus = (insured) => {
        return insured.maritalStatus || insured.policyContactMaritalStatus_Ext;
    };

    const getInsuredMaritalStatus = (insured) => {
        return !_.isUndefined(insured.policyContactMaritalStatus_Ext?.value) ? insured.policyContactMaritalStatus_Ext : insured.maritalStatus;
    };

    const getInsuredAge = (insured) => {
        if (insured.policyContactDateOfBirth_Ext) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return MLLocalDateUtil.differenceInYears(today, insured.policyContactDateOfBirth_Ext);
        }
        return insured.age_ML;
    };

    const getInsuredDateOfBirth = (insured) => {
        return insured.policyContactDateOfBirth_Ext ? insured.policyContactDateOfBirth_Ext : insured.dateOfBirth;
    };

    const getInsuredTitle = (nameAndTitle) => {
        let title = '';

        if (!nameAndTitle) {
            return '';
        }

        for (let i = nameAndTitle.length - 1; i >= 0; i--) {
            if (nameAndTitle[i] !== ',') {
                title = title.concat(nameAndTitle[i]);
            } else {
                title = title.concat(',');
                return title.split('').reverse().join('');
            }
        }

        return '';
    };

    const formatPhoneNumber = (number) => {
        if (_.isNil(number) || number.length !== 10) {
            return number;
        }

        const area = number.substring(0, 3);
        const front = number.substring(3, 6);
        const end = number.substring(6, 10);
        return `(${area}) ${front}-${end}`;
    }

    const getPrimaryLabel = () => {
        return deedInfo_Ext ? translator(messages.firstPersonInControl) : translator(messages.PrimaryInsured)
    }

    const getLabelForSni = () => {
        return deedInfo_Ext ? translator(messages.secondPersonInControl) : translator(messages.CoInsured)
    }

    const generatePrimaryNamedInsuredInfo = (insured) => {
        if (insured.isPrimaryNamedInsured_ML) {
            return (
                <p>
                    <i className="icon-check icon-teal" />
                    <span className="teal-text padding-left-5">{getPrimaryLabel()}</span>
                </p>
            );
        };
        return (
            <p>
                {getLabelForSni()}
            </p>
        );
    }

    const getTypeKeyDescription = (typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    };

    const getInsuredMaritalStatusByTypelistCode = (typelistCodes, code) => {
        const maritalStatus = _.find(typelistCodes, (item) => {
            return item.code === code;
        });
        return maritalStatus.name;
    };

    const generateMaritalStatusInfo = (insured) => {
        const gender = _.get(insuredsVM[insured.publicID], 'gender.value.name');
        let maritalStatus = '';
        if (insuredHasMaritalStatus(insured)) {
            if(_.isUndefined(insured.policyContactMaritalStatus_Ext)) {
                maritalStatus = _.get(getInsuredMaritalStatus(insuredsVM[insured.publicID]), 'value.name');
            } else {
                const maritalStatusCode = (insured.policyContactMaritalStatus_Ext) ? insured.policyContactMaritalStatus_Ext : insured.maritalStatus;
                const maritalStatusTypelistCodes = _.get(getInsuredMaritalStatus(insuredsVM[insured.publicID]), 'value.typelist.codes');
                maritalStatus = getInsuredMaritalStatusByTypelistCode(maritalStatusTypelistCodes, maritalStatusCode);
            }
            return (
                <p>{`${getTypeKeyDescription(gender) || ''}, ${getTypeKeyDescription(maritalStatus) || ''}`}</p>
            );
        };
        return (
            <p>{getTypeKeyDescription(gender)}</p>
        );
    }

    const generateGenderInfo = (insured) => {
        if (insured.gender) {
            return (
                <div>
                    {generateMaritalStatusInfo(insured)}
                </div>
            );
        };
        const maritalStatus = _.get(getInsuredMaritalStatus(insuredsVM[insured.publicID]), 'value.name');
        return (
            (maritalStatus !== undefined) ? <p>{getTypeKeyDescription(maritalStatus)}</p> : <></>
        );
    }

    const getSortedData = (dataToSort) => {
        const sortedData = dataToSort.sort((insured) => {
            const isPrimary = insured.isPrimaryNamedInsured_ML
            if (isPrimary) {
                return -1;
            }
            return 1;
        });

        return sortedData;
    };

    const generateAddressSection = (insured) => {
        if (!insured.sameAddressAsPH_ML) {
            return (
                <div className="col-md-4">
                    <div className="display-inline-block vertical-align-top padding-right-10">
                        <p>
                            <i className="icon-ml-location icon-met-gray-11" />
                        </p>
                    </div>
                    <div className="display-inline-block">
                        <p>
                            {insured.primaryAddress.addressLine1}
                        </p>
                        <p>
                            {`${insured.primaryAddress.city}, ${insured.primaryAddress.state}`} {insured.primaryAddress.postalCode}
                        </p>
                    </div>
                </div>
            );
        }
        return (
            <div className="col-md-4">
                <div className="display-inline-block vertical-align-top padding-right-10">
                    <i className="icon-ml-location icon-met-gray-11" />
                </div>
                <div className="display-inline-block">
                    <p>
                        {translator(messages.SameAddress)}
                    </p>
                </div>
            </div>

        );
    };

    const primaryPhoneType = (insured, number) => {
        if (insured.primaryPhoneType !== undefined && ((number === insured.homeNumber && insured.primaryPhoneType.indexOf('home') > -1) ||
            (number === insured.cellNumber && insured.primaryPhoneType.indexOf('mobile') > -1) ||
            (number === insured.workNumber && insured.primaryPhoneType.indexOf('work') > -1))) {
            return true;
        }
        return false;
    };

    const generateInsuredInfoSection = (insured) => {
        return (
            <div className={cx(styles.mlInsuredInformation, 'col-md-4')}>
                <div className={cx('display-inline-block', 'vertical-align-top', styles.widthMax)}>
                    {insured.contactNumbers.map((number, index) => {
                        return (
                            (number && insured.faxNumber !== number) ?
                                <p className="ml-contact-details">
                                    <i className="icon-ml-phone icon-met-gray-11 padding-right-10" />
                                    {formatPhoneNumber(number)}
                                    {(primaryPhoneType(insured, number)) ?
                                        <span>
                                            {(index === 0) ?
                                                <span>{translator(messages.Primary)}</span> : <></>
                                            }
                                        </span> : <></>
                                    }
                                </p> : <></>
                        );
                    })}
                </div>
                {(insured.faxNumber) ?
                    <div className={cx('display-inline-block', styles.widthMax)}>
                        <p className="ml-contact-details">
                            <i className="icon icon-fax icon-met-gray-11 padding-right-10" />
                            {insured.faxNumber}
                        </p>
                    </div> : <></>
                }
                <div className={cx('display-inline-block', styles.widthMax)}>
                    {insured.contactEmailAddresses.map((address, index) => {
                        return (
                            (address) ?
                                <p className="ml-contact-details">
                                    {(index === 0) ?
                                        <i className="icon-ml-letter icon-met-gray-11 padding-right-10" /> : <></>
                                    }
                                    {address}
                                </p> : <></>
                        );
                    })}
                </div>
            </div>
        );
    };

    const toLocalDate = (date) => {
        return moment(date).format('MM/DD/YYYY');
    };

    if (show) {
        return (
            <div>
                <h2 className="section-header vertical-spacing-20">
                    {translator(messages.InsuredInfo)}
                </h2>
                { getSortedData(data).map((insured) => {
                    return (
                        <div className="vertical-spacing-40">
                            <div className={cx(styles.displayFlex, 'row')}>
                                <div className="col-md-4 icon-indent">
                                    <span className={cx(styles.pullLeft, 'icon-ml-for-gender')}>
                                        <i className="icon icon-foremost-account-icon account-icon-left-rail" />
                                    </span>
                                    <div className={cx(styles.pullLeft)}>
                                        <div className="vertical-spacing-10">
                                            <p><strong>{getInsuredName(insured)}{getInsuredTitle(insured.displayName)}</strong></p>
                                            {generatePrimaryNamedInsuredInfo(insured)}
                                        </div>
                                        {generateGenderInfo(insured)}
                                        <p>{`${getInsuredAge(insured)}, ${toLocalDate(getInsuredDateOfBirth(insured))}`}</p>
                                    </div>
                                </div>
                                {generateAddressSection(insured)}
                                {generateInsuredInfoSection(insured)}
                            </div>
                        </div>
                    );
                })}
                <hr className="vertical-spacing-40" />
            </div>
        );
    }
    return (<></>);
}

MLInsured.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({})),
    insuredsVM: PropTypes.shape({}),
    show: PropTypes.bool
};
MLInsured.defaultProps = {
    data: [],
    insuredsVM: {},
    show: false
};
export default MLInsured;
